import i18n from "locale/i18n";
import { store } from "../../store";
import eventBus from "../../eventBus";
import { ApiClient } from "../../api/ApiClient";
import { enqueueItem, SyncItem, SyncItemType } from "./SyncActions";
import { apiErrorCodes } from "../../api/ApiResponse";
import {
  rehydrateRecapData,
  rehydratePortfoliosData,
  BLACK_FEATURE_TRIGGERED,
  SET_SITE_CONFIG,
  SET_USER_ERROR,
  SET_SIGN_IN_WITH_GOOGLE_FLOW,
  SET_SIGNIN_REDIRECT_PATH,
  FETCH_MULTIUSER_LIST_PENDING,
  SET_USER_PREFERENCES,
  SET_MFA_PREFERENCE,
  REMOVE_STRIPE_CONNECTED_ACCOUNT,
  SET_STATUS_CONFIG,
  SET_USER,
  SET_SIGN_UP_WITH_GOOGLE_FLOW,
  FETCH_CREDIT_BALANCE_PENDING,
  UPDATE_CREDIT_BALANCE,
  SET_APP_MAINTENANCE_STATUS,
  SHOW_BREAKING_CHANGES,
  SET_MULTIUSER_LIST,
  SET_MULTIUSER_INVITATION_DATA,
  SET_RECEIPTS,
  SET_USER_OAUTH_CONNECTIONS,
  SET_USER_API_KEYS,
  userApiKeyTypes,
  SET_SHARE_MASK,
  userMaskAllValuesSelector,
  updatePortfolioAction,
  toastType,
  setPageReloadingAction
} from "./Common";
import {
  authReducerInitialState as initialState,
  multiuserListSelector,
  showBlackPaywallsSelector,
  userAPIKeysSelector,
  userOAuthConnectionsSelector,
  userTimezoneSelector
} from "../reducers/AuthReducer";
import {
  userSelector,
  getPortfolioSessionUserId,
  userPreferencesSelector,
  portfoliosReducerName,
  siteConfigSelector,
  setPortfolioSessionUserId,
  defaultPersonTagSelector
} from "../reducers/Common";
import {
  portfolioPersistTransform,
  portfolioReducer,
  initialState as portfoliosInitialState
} from "../reducers/PortfolioReducer";
import { getUuid, isAppVersionLesserThan } from "../../utilities/Number";
import { setUserNameForViewMode, getRecaptchaToken, userTypes } from "../../utilities/Common";
import {
  isAppInWhiteLabelMode,
  getSiteWhiteLabelConfigId,
  isAppInViewMode,
  getTokenForViewMode
} from "../../utilities/Common";
import { runShareChallenge, VIEW_CHALLENGE_ACTION } from "../../utilities/ShareChallenge";
import { isMobile } from "../../utilities/Location";
import { getUtcOffsetForTimezone } from "../../utilities/Timezone";
import DeferredPromise from "../../utilities/DeferredPromise";
import { Toast, showToastAction, dismissToastAction } from "./ToastActions";
import AmplifyAuth, { formatPhoneNumber, getUserAttributes } from "../../utilities/AmplifyAuth";

export const NOT_AUTHORIZED_ERROR_CODE = "NotAuthorizedException";

export const userTypeToString = userType => {
  switch (userType) {
    case userTypes.OWNER:
      return "Account Owner";
    case userTypes.ADMIN:
      return "Account Admin";
    case userTypes.MANAGER:
      return "Account User";
    default:
      return "User";
  }
};

export const userTypeToDescriptionString = userType => {
  switch (userType) {
    case userTypes.OWNER:
      return "Owner";
    case userTypes.ADMIN:
      return "Can add other users to this account.\nCan invite clients\nHave access to client dashboard.";
    case userTypes.MANAGER:
      return "Can invite clients.\nHave access to client dashboard.";
    default:
      return "User";
  }
};

export const SUBSCRIPTION_ERROR = Object.freeze({
  CONFIRMPAYMENT_ERROR: "SUBSCRIPTION_CONFIRMPAYMENT_ERROR",
  CONFIRMPAYMENT_STATUSERROR: "SUBSCRIPTION_CONFIRMPAYMENT_STATUSERROR",
  CONFIRMSETUP_ERROR: "SUBSCRIPTION_CONFIRMSETUP_ERROR",
  CONFIRMSETUP_STATUSERROR: "SUBSCRIPTION_CONFIRMSETUP_STATUSERROR"
});

export const SUBSCRIPTION_ERROR_CODES = Object.freeze({
  WENT_WRONG: 1001,
  DECLINED: 1024,
  DUPLICATE: 1062
});

export const confirmCardSetupErrorCodes = Object.freeze({
  SETUP_INTENT_AUTHENTICATION_FAILURE: "setup_intent_authentication_failure"
});

export const SIDEBAR_CHANGES_TIMESTAMP = 1695410779;

export const setSiteConfigAction = config => ({
  type: SET_SITE_CONFIG,
  config
});

export const removeStripeConnectedAccountAction = config => ({
  type: REMOVE_STRIPE_CONNECTED_ACCOUNT,
  config
});

export const setStatusConfigAction = config => ({
  type: SET_STATUS_CONFIG,
  config
});

export const setUserAction = user => ({
  type: SET_USER,
  user
});

export const setUserErrorAction = userError => ({
  type: SET_USER_ERROR,
  userError
});

let lastSetUserPreferencesJson = null;

const serializeUserPreferences = userPreferences =>
  JSON.stringify({ ...initialState.userPreferences, ...userPreferences });

export const setUserPreferencesAction = userPreferences => {
  if (Object.keys(userPreferences).length > 0) {
    lastSetUserPreferencesJson = serializeUserPreferences(userPreferences);
  }
  return {
    type: SET_USER_PREFERENCES,
    userPreferences
  };
};

export const setSignInRedirectPathAction = path => ({
  type: SET_SIGNIN_REDIRECT_PATH,
  path
});

export const setSignInWithGoogleFlowAction = isSignInWithGoogleFlow => ({
  type: SET_SIGN_IN_WITH_GOOGLE_FLOW,
  isSignInWithGoogleFlow
});

export const setSignUpWithGoogleFlowAction = isSignUpWithGoogleFlow => ({
  type: SET_SIGN_UP_WITH_GOOGLE_FLOW,
  isSignUpWithGoogleFlow
});

export const fetchCreditBalancePendingAction = isPending => ({
  type: FETCH_CREDIT_BALANCE_PENDING,
  isPending
});

export const updateCreditBalanceAction = balanceInCents => ({
  type: UPDATE_CREDIT_BALANCE,
  balanceInCents
});

export const setAppMaintenanceStatus = isAppInMaintenanceMode => ({
  type: SET_APP_MAINTENANCE_STATUS,
  isAppInMaintenanceMode
});

export const setShowBreakingChangesError = () => ({
  type: SHOW_BREAKING_CHANGES
});

export const fetchMultiuserListPending = isPending => ({
  type: FETCH_MULTIUSER_LIST_PENDING,
  isPending
});

export const setMultiUserList = list => ({
  type: SET_MULTIUSER_LIST,
  list
});

export const setMultiuserInvitationData = (email, invitationId, portfolioId) => ({
  type: SET_MULTIUSER_INVITATION_DATA,
  email,
  invitationId,
  portfolioId
});

export const setReceipts = receipts => ({
  type: SET_RECEIPTS,
  receipts
});

export const setShareMaskAction = shareMaskValue => ({
  type: SET_SHARE_MASK,
  shareMaskValue
});

const featureBlackTriggerAction = (promise, action) => ({
  type: BLACK_FEATURE_TRIGGERED,
  promise,
  action
});

export const showBlackPaywallIfQualifies = (action = "DEFAULT") => {
  return (dispatch, getState) => {
    if (showBlackPaywallsSelector(getState())) {
      const promise = new DeferredPromise();
      dispatch(featureBlackTriggerAction(promise, action));
      return promise;
    }

    return true;
  };
};

export const deferUntilUserIsSet = new DeferredPromise();
export const fetchUser = (onSuccess, onError) => {
  return (dispatch, getState) => {
    ApiClient.getUser(getUuid())
      .then(apiData => {
        const user = apiData.payload;

        const storedUser = userSelector(getState());
        if (!storedUser === false && storedUser.id !== user.id) {
          eventBus.dispatch("signOut");
          return;
        }

        dispatch(setUserAction(user));

        if (!user.sharedWithMe === false) {
          injectReducersForSharedPortfolios(user.sharedWithMe);
        }

        deferUntilUserIsSet.resolve();

        const portfolioSessionId = getPortfolioSessionUserId();
        if (
          !portfolioSessionId === false &&
          portfolioSessionId !== user.id &&
          (!user.sharedWithMe === true || !user.sharedWithMe.find(item => item.id === portfolioSessionId) === true)
        ) {
          dispatch(switchToUsersDefaultAccount());
          return;
        }

        if (!portfolioSessionId === true) {
          setPortfolioSessionUserId(user.id);
        }

        if (!onSuccess === false) {
          onSuccess(user);
        }
      })
      .catch(apiError => {
        if (isAppInViewMode() === false) {
          dispatch(setUserErrorAction(apiError));
        }
        // K-4688: release any fetchPortfolios calls awaiting deferUntilUserIsSet so they
        // don't hang forever when fetchUser fails
        deferUntilUserIsSet.resolve();
        if (!onError === false) {
          onError(apiError);
        }
      });
  };
};

export const verifyEmail = (name, email) => {
  return dispatch => {
    return new Promise((resolve, reject) => {
      ApiClient.verifyEmail(getUuid(), name, email)
        .then(apiData => {
          resolve(apiData.payload);
        })
        .catch(apiError => {
          reject(apiError);
        });
    });
  };
};

export const validateEmail = (name, email, verificationCode) => {
  return dispatch => {
    return new Promise((resolve, reject) => {
      ApiClient.validateEmail(getUuid(), name, email, verificationCode)
        .then(apiData => {
          const user = apiData.payload;

          // For WL users set the subscription data
          // as if the subscription is active and will
          // never expire
          if (!user.wl === false) {
            user.subscription = {
              status: "active",
              tsStart: user.tsCreated,
              tsEnd: 1929610173,
              remainingDays: 1000000,
              graceDays: 0
            };
          }

          dispatch(setUserAction(user));
          resolve(user);
        })
        .catch(apiError => {
          reject(apiError);
        });
    });
  };
};

export const isUserValid = (onSuccess, onError) => {
  return dispatch => {
    ApiClient.isUserValid(getUuid())
      .then(apiData => {
        onSuccess(apiData.payload.valid === 1);
      })
      .catch(apiError => {
        onError(apiError);
      });
  };
};

export const updateUser = (user, isUserAction = true) => {
  return dispatch => {
    if (user.phone && /^\+/.test(user.phone) === false) {
      user.phone = `+${user.phone}`;
    }
    dispatch(setUserAction(user));

    const request = idempotentId => ApiClient.setUser(idempotentId, user);
    const syncItem = new SyncItem(SyncItemType.UPDATE, "update_user", request, 0, true, apiData => {});
    dispatch(enqueueItem(syncItem, false, isUserAction));
  };
};

export const updateUserAttributes = (propertiesToUpdate, onSuccess, onError) => {
  return (dispatch, getState) => {
    const user = { ...userSelector(getState()), ...propertiesToUpdate };

    ApiClient.setUser(getUuid(), user)
      .then(apiData => {
        const user = apiData.payload;
        dispatch(setUserAction(user));
        onSuccess(user);
      })
      .catch(apiError => {
        onError(apiError);
      });
  };
};

export const userSetPasswordUpdated = () => {
  return dispatch => {
    return new Promise((resolve, reject) => {
      const request = idempotentId =>
        ApiClient.userSetPasswordUpdated(idempotentId)
          .then(apiData => {
            const user = apiData.payload;
            dispatch(setUserAction(user));
            resolve(user);
          })
          .catch(apiError => {
            reject(apiError);
          });

      const syncItem = new SyncItem(SyncItemType.UPDATE, "set_password", request, 0, true, apiData => {});
      dispatch(enqueueItem(syncItem));
    });
  };
};

export const getUserProfilePhotoUrl = (onSuccess, onError, pictureToken = undefined) => {
  return (dispatch, getState) => {
    const user = userSelector(getState());

    if (!user === true) {
      return "";
    }
    const profilePicture = pictureToken === undefined ? user.picture : pictureToken;
    if (!profilePicture === true) {
      onSuccess("");
    } else if (profilePicture.startsWith("http") === true) {
      onSuccess(profilePicture);
    } else {
      ApiClient.getProfilePictureDownloadUrl(profilePicture)
        .then(url => {
          onSuccess(url);
        })
        .catch(onError);
    }
  };
};

export const uploadProfilePhoto = (file, onSuccess, onError) => {
  return dispatch => {
    ApiClient.uploadProfilePhoto(getUuid(), file)
      .then(apiData => {
        const user = apiData.payload;
        dispatch(setUserAction(user));
        onSuccess(user);
      })
      .catch(apiError => {
        onError(apiError);
      });
  };
};

export const uploadPortfolioPhoto = (portfolioId, file, onSuccess, onError) => {
  return dispatch => {
    const toast = new Toast(toastType.TIP, i18n.t("portfolioTree.uploadingProfilePhoto"), 5000, null, null);
    dispatch(showToastAction(toast));

    const dismissToast = () => {
      setTimeout(() => {
        dispatch(dismissToastAction());
      }, 1000);
    };
    ApiClient.uploadPortfolioPhoto(getUuid(), portfolioId, file)
      .then(apiData => {
        const portfolio = apiData.payload;
        dispatch(updatePortfolioAction(portfolio, "picture"));
        onSuccess(portfolio);
        dismissToast();
      })
      .catch(apiError => {
        onError(apiError);
        const toast = new Toast(toastType.GENERIC_ERROR, i18n.t("portfolioTree.imageUploadFailed"), 2500, null, null);
        dispatch(showToastAction(toast));
      });
  };
};

export const requestAccountDeletion = (onSuccess, onError) => {
  return dispatch => {
    ApiClient.requestAccountDeletion(getUuid())
      .then(apiData => {
        onSuccess();
      })
      .catch(apiError => {
        onError(apiError);
      });
  };
};

export const updateUserTimezoneAndOffset = () => {
  return (dispatch, getState) => {
    if (isAppInViewMode()) {
      return;
    }

    const user = userSelector(getState());
    if (!user === true) {
      return;
    }

    const tz = userTimezoneSelector(getState());
    const utcOffset = getUtcOffsetForTimezone(tz);
    if (user.utcOffset === utcOffset && user.tz === tz) {
      return;
    }
    dispatch(updateUser({ ...user, utcOffset, tz }, false));
  };
};

export const updateUserPreferences = (
  preferencesToUpdate,
  syncItemId,
  delay,
  fetchLatestBeforeUpdate = false,
  isUserAction = true
) => {
  return (dispatch, getState) => {
    const siteConfig = siteConfigSelector(getState());
    if (
      siteConfig.mask === 1 &&
      ("maskAllValues" in preferencesToUpdate || "maskAllValuesMobile" in preferencesToUpdate)
    ) {
      const maskAllValues = userMaskAllValuesSelector(getState());
      delete preferencesToUpdate.maskAllValues;
      delete preferencesToUpdate.maskAllValuesMobile;
      dispatch(setShareMaskAction(!maskAllValues));
    }

    const currentPreferences = userPreferencesSelector(getState());
    const updatedPreferences = { ...initialState.userPreferences, ...currentPreferences, ...preferencesToUpdate };
    if (serializeUserPreferences(updatedPreferences) === lastSetUserPreferencesJson) {
      return;
    }

    dispatch(setUserPreferencesAction(updatedPreferences));
    if (isAppInViewMode()) {
      return;
    }

    const queueUpdate = preferences => {
      const request = idempotentId => ApiClient.setUserPreferences(idempotentId, preferences);
      const syncItem = new SyncItem(
        SyncItemType.UPDATE,
        syncItemId || "update_user_preferences",
        request,
        delay || 0,
        true,
        apiData => {}
      );
      dispatch(enqueueItem(syncItem, true, isUserAction));
    };

    if (fetchLatestBeforeUpdate === true) {
      ApiClient.getUserPreferences(getUuid())
        .then(apiData => {
          if (!apiData.payload === true) {
            queueUpdate(updatedPreferences);
            return;
          }

          const preferences = { ...apiData.payload, ...preferencesToUpdate };
          dispatch(setUserPreferencesAction(preferences));
          queueUpdate(preferences);
        })
        .catch(apiError => {
          queueUpdate(updatedPreferences);
        });
    } else {
      queueUpdate(updatedPreferences);
    }
  };
};

export const deleteUserAccount = (token, onSuccess, onError, retryCount = 0) => {
  return dispatch => {
    ApiClient.deleteUserAccount(getUuid(), token)
      .then(apiData => {
        onSuccess();
      })
      .catch(apiError => {
        if (apiError.errorCode === apiErrorCodes.DELETE_IN_PROGRESS && retryCount < 5) {
          setTimeout(() => {
            dispatch(deleteUserAccount(token, onSuccess, onError, retryCount + 1));
          }, 1000);
          return;
        }
        onError(apiError);
      });
  };
};

export const markUserActive = (token, onSuccess, onError) => {
  return dispatch => {
    ApiClient.markUserActive(getUuid(), token)
      .then(apiData => {
        onSuccess();
      })
      .catch(apiError => {
        onError(apiError);
      });
  };
};

export const requestUserPhoneUpdateOtp = (phone, onSuccess, onError) => {
  return dispatch => {
    ApiClient.requestUserPhoneUpdateOtp(getUuid(), formatPhoneNumber(phone))
      .then(apiData => {
        onSuccess();
      })
      .catch(apiError => {
        onError(apiError);
      });
  };
};

export const submitUserPhoneUpdateOtp = (phone, otp, onSuccess, onError) => {
  return dispatch => {
    ApiClient.submitUserPhoneUpdateOtp(getUuid(), formatPhoneNumber(phone), otp)
      .then(apiData => {
        onSuccess();
      })
      .catch(apiError => {
        onError(apiError);
      });
  };
};

export const removeUserPhoneNumber = () => {
  return (dispatch, getState) => {
    const user = userSelector(getState());
    user.phone = null;

    dispatch(setUserAction(user));

    const request = idempotentId => ApiClient.removeUserPhoneNumber(idempotentId);
    const syncItem = new SyncItem(SyncItemType.UPDATE, "remove_user_phone", request, 0, true, apiData => {});
    dispatch(enqueueItem(syncItem));
  };
};

export const subscribeUserEmail = (email, source, onSuccess, onError) => {
  return dispatch => {
    ApiClient.subscribeUserEmail(getUuid(), encodeURIComponent(email), source)
      .then(apiData => {
        onSuccess();
      })
      .catch(apiError => {
        onError(apiError);
      });
  };
};

export const getClientInfo = (onSuccess, onError) => {
  return dispatch => {
    ApiClient.getClientInfo(getUuid(), window.location.host === "beta.kubera.com" ? 1 : 0)
      .then(apiData => {
        onSuccess(apiData.payload);
      })
      .catch(apiError => {
        if (onError) {
          onError(apiError);
        }
      });
  };
};

const showAppUpdateToast = () => {
  return dispatch => {
    const toast = new Toast(
      toastType.TIP,
      isMobile() ? "New Version Available." : "Get latest improvements.",
      -1,
      () => {
        if (isMobile()) {
          dispatch(setPageReloadingAction(true));
        }
        setTimeout(() => {
          window.location.reload();
        }, 100);
      },
      null
    );
    toast.actionButtonTitle = isMobile() ? "Update" : "Refresh";
    toast.showDismissButton = isMobile();
    toast.dismissOnOutsideClick = false;
    dispatch(showToastAction(toast));
  };
};
export const showAppUpdateToastIfNeeded = () => {
  return dispatch => {
    dispatch(
      getClientInfo(apiData => {
        if (isAppVersionLesserThan(apiData.lastBreakingVersion) === true) {
          dispatch(setShowBreakingChangesError());
        } else if (isAppVersionLesserThan(apiData.version) === true) {
          dispatch(showAppUpdateToast());
        }
      }, null)
    );
  };
};

export const getTrialInfo = () => {
  return dispatch => {
    return new Promise((resolve, reject) => {
      const request = idempotentId =>
        ApiClient.getTrialInfo(idempotentId)
          .then(apiData => {
            dispatch(setUserAction(apiData.payload.user));

            resolve(apiData);
          })
          .catch(apiError => {
            reject(apiError);
          });

      const syncItem = new SyncItem(SyncItemType.UPDATE, "get_trial", request, 0, true, apiData => {});
      dispatch(enqueueItem(syncItem));
    });
  };
};

export const startTrial = paymentMethodId => {
  return dispatch => {
    return new Promise((resolve, reject) => {
      const request = idempotentId =>
        ApiClient.startTrial(idempotentId, {
          paymentMethodId
        })
          .then(apiData => {
            dispatch(setUserAction(apiData.payload.user));
            eventBus.dispatch("fetchPortfolios", [false, true]);

            resolve(apiData);
          })
          .catch(apiError => {
            reject(apiError);
          });

      const syncItem = new SyncItem(SyncItemType.CREATE, "start_trial", request, 0, true, apiData => {});
      dispatch(enqueueItem(syncItem));
    });
  };
};

export const createSubscription = (paymentMethodId, subscriptionFreaquency, discountCode) => {
  return (dispatch, getState) => {
    return new Promise((resolve, reject) => {
      const request = idempotentId =>
        ApiClient.createSubscription(idempotentId, {
          paymentMethodId,
          plan: subscriptionFreaquency,
          discountCode
        })
          .then(apiData => {
            dispatch(
              setUserAction({
                ...getState().auth.user,
                subscription: apiData.payload
              })
            );
            eventBus.dispatch("fetchPortfolios", [false, true]);

            resolve(apiData);
          })
          .catch(apiError => {
            reject(apiError);
          });

      const syncItem = new SyncItem(SyncItemType.CREATE, "create_subscription", request, 0, true, apiData => {});
      dispatch(enqueueItem(syncItem));
    });
  };
};

export const upgradeSubscription = subscriptionFreaquency => {
  return (dispatch, getState) => {
    return new Promise((resolve, reject) => {
      const request = idempotentId =>
        ApiClient.upgradeSubscription(idempotentId, {
          plan: subscriptionFreaquency
        })
          .then(apiData => {
            dispatch(
              setUserAction({
                ...getState().auth.user,
                subscription: apiData.payload
              })
            );
            eventBus.dispatch("fetchPortfolios", [false, true]);

            resolve(apiData);
          })
          .catch(apiError => {
            reject(apiError);
          });

      const syncItem = new SyncItem(SyncItemType.CREATE, "upgrade_subscription", request, 0, true, apiData => {});
      dispatch(enqueueItem(syncItem));
    });
  };
};

export const cancelSubscription = () => {
  return (dispatch, getState) => {
    return new Promise((resolve, reject) => {
      const request = idempotentId =>
        ApiClient.cancelSubscription(idempotentId)
          .then(apiData => {
            dispatch(
              setUserAction({
                ...getState().auth.user,
                subscription: apiData.payload
              })
            );

            resolve(apiData);
          })
          .catch(apiError => {
            reject(apiError);
          });

      const syncItem = new SyncItem(SyncItemType.CREATE, "cancel_subscription", request, 0, true, apiData => {});
      dispatch(enqueueItem(syncItem));
    });
  };
};

export const detachSubscriptionCard = () => {
  return (dispatch, getState) => {
    return new Promise((resolve, reject) => {
      const request = idempotentId =>
        ApiClient.detachSubscriptionCard(idempotentId)
          .then(apiData => {
            if (isAppInWhiteLabelMode() === false) {
              dispatch(
                setUserAction({
                  ...getState().auth.user,
                  subscription: apiData.payload
                })
              );
            }

            resolve(apiData);
          })
          .catch(apiError => {
            reject(apiError);
          });

      const syncItem = new SyncItem(SyncItemType.CREATE, "detach_subscription_card", request, 0, true, apiData => {
        if (isAppInWhiteLabelMode() !== false) {
          setTimeout(() => {
            window.location.reload();
          }, 10);
        }
      });
      dispatch(enqueueItem(syncItem));
    });
  };
};

export const updateSubscriptionCard = paymentMethodId => {
  return (dispatch, getState) => {
    return new Promise((resolve, reject) => {
      const request = idempotentId =>
        ApiClient.updateSubscriptionCard(idempotentId, {
          paymentMethodId
        })
          .then(apiData => {
            dispatch(
              setUserAction({
                ...getState().auth.user,
                subscription: apiData.payload
              })
            );

            resolve(apiData);
          })
          .catch(apiError => {
            reject(apiError);
          });

      const syncItem = new SyncItem(SyncItemType.CREATE, "update_subscription_card", request, 0, true, apiData => {});
      dispatch(enqueueItem(syncItem));
    });
  };
};

export const wlUpdateSubscriptionCard = paymentMethodId => {
  return dispatch => {
    return new Promise((resolve, reject) => {
      const request = idempotentId =>
        ApiClient.updateSubscriptionCard(idempotentId, {
          paymentMethodId
        })
          .then(apiData => {
            resolve(apiData);
          })
          .catch(apiError => {
            reject(apiError);
          });

      const syncItem = new SyncItem(SyncItemType.CREATE, "update_subscription_card", request, 0, true, apiData => {});
      dispatch(enqueueItem(syncItem));
    });
  };
};

export const wlUpdateUnverifiedSubscriptionCard = (setupIntentId, paymentMethodId) => {
  return dispatch => {
    return new Promise((resolve, reject) => {
      const request = idempotentId =>
        ApiClient.updateUnverifiedSubscriptionCard(idempotentId, {
          paymentMethodId,
          setupIntentId
        })
          .then(apiData => {
            resolve(apiData);
          })
          .catch(apiError => {
            reject(apiError);
          });

      const syncItem = new SyncItem(SyncItemType.CREATE, "update_subscription_card", request, 0, true, apiData => {});
      dispatch(enqueueItem(syncItem));
    });
  };
};

export const retrySubscription = paymentMethodId => {
  return (dispatch, getState) => {
    return new Promise((resolve, reject) => {
      const request = idempotentId =>
        ApiClient.retrySubscription(idempotentId, {
          paymentMethodId
        })
          .then(apiData => {
            dispatch(
              setUserAction({
                ...getState().auth.user,
                subscription: apiData.payload
              })
            );

            resolve(apiData);
          })
          .catch(apiError => {
            reject(apiError);
          });

      const syncItem = new SyncItem(SyncItemType.CREATE, "retry_subscription", request, 0, true, apiData => {});
      dispatch(enqueueItem(syncItem));
    });
  };
};

export const setupPaymentIntent = subscriptionFreaquency => {
  return dispatch => {
    return new Promise((resolve, reject) => {
      ApiClient.setupPaymentIntent(getUuid(), {
        plan: subscriptionFreaquency
      })
        .then(apiData => {
          resolve(apiData);
        })
        .catch(apiError => {
          reject(apiError);
        });
    });
  };
};

export const getSubscriptionState = () => {
  return (dispatch, getState) => {
    return new Promise((resolve, reject) => {
      ApiClient.getSubscriptionState(getUuid())
        .then(apiData => {
          dispatch(
            setUserAction({
              ...getState().auth.user,
              subscription: apiData.payload
            })
          );

          resolve(apiData);
        })
        .catch(apiError => {
          reject(apiError);
        });
    });
  };
};

export const exportAndDelete = () => {
  return dispatch => {
    return new Promise((resolve, reject) => {
      ApiClient.exportAndDelete(getUuid())
        .then(apiData => {
          resolve(apiData);
        })
        .catch(apiError => {
          reject(apiError);
        });
    });
  };
};

export const generateBackupCodes = () => {
  return dispatch => {
    return new Promise((resolve, reject) => {
      ApiClient.generateBackupCodes(getUuid())
        .then(apiData => {
          resolve(apiData);
        })
        .catch(apiError => {
          reject(apiError);
        });
    });
  };
};

export const userPhoneSet = phone => {
  return dispatch => {
    let formattedPhoneNumber = formatPhoneNumber(phone);
    return new Promise((resolve, reject) => {
      ApiClient.userPhoneSet(getUuid(), {
        phone: formattedPhoneNumber
      })
        .then(apiData => {
          resolve(apiData);
        })
        .catch(apiError => {
          reject(apiError);
        });
    });
  };
};

export const verifyUserBackupCode = (username, password, backupCode) => {
  return (dispatch, getState) => {
    return new Promise((resolve, reject) => {
      const siteConfig = siteConfigSelector(getState());
      const clientId = siteConfig.clientId;
      const userPoolId = siteConfig.userPoolId;

      ApiClient.verifyUserBackupCode(getUuid(), {
        username,
        password,
        backupCode,
        clientId,
        userPoolId
      })
        .then(apiData => {
          resolve(apiData);
        })
        .catch(apiError => {
          reject(apiError);
        });
    });
  };
};

export const userSetMFA = type => {
  return dispatch => {
    return new Promise((resolve, reject) => {
      ApiClient.userSetMFA(getUuid(), type)
        .then(apiData => {
          resolve(apiData);
        })
        .catch(apiError => {
          reject(apiError);
        });
    });
  };
};

export const getPreferredMFA = async () => {
  const attrs = await getUserAttributes();
  const preferredMFA = await AmplifyAuth.getPreferredMFA();
  return { default: preferredMFA, attrs };
};

let mfaPreferenceFetchedThisSession = false;
let mfaPreferenceFetchInFlight = null;

export const setMfaPreferenceAction = (preference, attrs) => ({
  type: SET_MFA_PREFERENCE,
  payload: { preference, attrs }
});

export const resetMfaPreferenceCache = () => dispatch => {
  mfaPreferenceFetchedThisSession = false;
  mfaPreferenceFetchInFlight = null;
};

export const refreshMfaPreference = () => async dispatch => {
  try {
    const { default: preference, attrs } = await getPreferredMFA();
    mfaPreferenceFetchedThisSession = true;
    dispatch(setMfaPreferenceAction(preference, attrs));
    return { preference, attrs };
  } catch (err) {
    mfaPreferenceFetchedThisSession = false;
    throw err;
  }
};

export const refreshMfaPreferenceIfNeeded = () => (dispatch, getState) => {
  const cached = getState().auth?.mfaPreference;
  if (mfaPreferenceFetchedThisSession && cached !== null) {
    return Promise.resolve(null);
  }
  if (mfaPreferenceFetchInFlight) {
    return mfaPreferenceFetchInFlight;
  }
  mfaPreferenceFetchInFlight = dispatch(refreshMfaPreference()).finally(() => {
    mfaPreferenceFetchInFlight = null;
  });
  return mfaPreferenceFetchInFlight;
};

export const setSiteConfig = (onSuccess, onError) => {
  return dispatch => {
    const saveConfig = config => {
      dispatch(setSiteConfigAction(config));
      // The timeout is to ensure that the site config is available to components
      // belonging to the onSuccess callback
      setTimeout(() => {
        onSuccess(config);
      }, 0);
    };

    if (isAppInWhiteLabelMode() === false) {
      const defaultConfig = {
        name: "Kubera",
        id: "kubera",
        website: process.env.REACT_APP_WEB_HOMEPAGE_URL + "/?r",
        userPoolId: process.env.REACT_APP_COGNITO_POOL_ID,
        clientId: process.env.REACT_APP_COGNITO_CLIENT_ID,
        status: "active"
      };
      defaultConfig.logoUrl = process.env.REACT_APP_LOGO_URL.replace("{{id}}", defaultConfig.id);
      defaultConfig.bigLogoUrl = process.env.REACT_APP_BIG_LOGO_URL.replace("{{id}}", defaultConfig.id);
      defaultConfig.darkLogoUrl = process.env.REACT_APP_BIG_LOGO_DARKMODE_URL.replace("{{id}}", defaultConfig.id);
      defaultConfig.mobileLogoUrl = process.env.REACT_APP_MOBILE_LOGO_URL.replace("{{id}}", defaultConfig.id);
      defaultConfig.mobileDarkLogoUrl = process.env.REACT_APP_MOBILE_DARK_LOGO_URL.replace("{{id}}", defaultConfig.id);

      if (isAppInViewMode()) {
        runShareChallenge(VIEW_CHALLENGE_ACTION, (recaptchaToken, type) =>
          ApiClient.getViewOnlySiteConfig(getUuid(), getTokenForViewMode(), recaptchaToken, type)
        )
          .then(apiData => {
            const viewModeConfig = apiData.payload;
            delete viewModeConfig.id;
            saveConfig({ ...viewModeConfig, ...defaultConfig });

            if (!viewModeConfig.userName === false || !viewModeConfig.userEmail === false) {
              setUserNameForViewMode(viewModeConfig.userName || viewModeConfig.userEmail);
            }
          })
          .catch(apiError => {
            onError(apiError);
          });
      } else {
        saveConfig(defaultConfig);
      }
    } else {
      ApiClient.getWlSiteConfig(getUuid(), getSiteWhiteLabelConfigId())
        .then(apiData => {
          const config = apiData.payload;
          const logoFolder = config.alias
            ? config.alias
            : config.id.length === 36 && config.id.endsWith("kkka")
            ? "kkka"
            : config.id;
          config.logoUrl = process.env.REACT_APP_LOGO_URL.replace("{{id}}", logoFolder);
          config.bigLogoUrl = process.env.REACT_APP_BIG_LOGO_URL.replace("{{id}}", logoFolder);
          config.darkLogoUrl = process.env.REACT_APP_BIG_LOGO_DARKMODE_URL.replace("{{id}}", logoFolder);
          config.mobileLogoUrl = process.env.REACT_APP_MOBILE_LOGO_URL.replace("{{id}}", logoFolder);
          config.mobileDarkLogoUrl = process.env.REACT_APP_MOBILE_DARK_LOGO_URL.replace("{{id}}", logoFolder);

          if (isAppInViewMode()) {
            runShareChallenge(VIEW_CHALLENGE_ACTION, (recaptchaToken, type) =>
              ApiClient.getViewOnlySiteConfig(getUuid(), getTokenForViewMode(), recaptchaToken, type)
            )
              .then(apiData => {
                const viewModeConfig = apiData.payload;
                delete viewModeConfig.id;
                saveConfig({ ...viewModeConfig, ...config });

                if (!viewModeConfig.userName === false || !viewModeConfig.userEmail === false) {
                  setUserNameForViewMode(viewModeConfig.userName || viewModeConfig.userEmail);
                }
              })
              .catch(apiError => {
                onError(apiError);
              });
          } else {
            saveConfig(config);
          }
        })
        .catch(apiError => {
          onError(apiError);
        });
    }
  };
};

export const validateWlDomainConfig = (host, onSuccess, onError) => {
  return () => {
    const whiteLabelId = getSiteWhiteLabelConfigId(host);
    if (!whiteLabelId === true) {
      onError();
      return;
    }
    ApiClient.getWlSiteConfig(getUuid(), whiteLabelId)
      .then(apiData => {
        const config = apiData.payload;
        if (!config === false && !config.id === false) {
          onSuccess(config);
        } else {
          onError();
        }
      })
      .catch(() => {
        onError();
      });
  };
};

export const setUserCollaborateDocumentStatus = status => {
  return (dispatch, getState) => {
    const user = userSelector(getState());
    user.wl.collaborateDocument = status;
    dispatch(setUserAction(user));

    const request = idempotentId => ApiClient.setUserCollaborateDocumentStatus(idempotentId, status);
    const syncItem = new SyncItem(SyncItemType.UPDATE, "update_user", request, 0, true, apiData => {});
    dispatch(enqueueItem(syncItem));
  };
};

export const verifyPasscode = (passcode, onSuccess, onError) => {
  return dispatch => {
    ApiClient.verifyPasscode(getUuid(), passcode)
      .then(apiData => {
        onSuccess(apiData.payload.config);
      })
      .catch(apiError => {
        onError(apiError);
      });
  };
};

export const applyPromoCode = (promoCode, onSuccess, onError) => {
  return dispatch => {
    ApiClient.applyPromoCode(getUuid(), promoCode)
      .then(apiData => {
        onSuccess(apiData.payload);
      })
      .catch(apiError => {
        onError(apiError);
      });
  };
};

export const updateUserSubscriptionAction = subscription => {
  return (dispatch, getState) => {
    dispatch(
      setUserAction({
        ...getState().auth.user,
        subscription: subscription
      })
    );
  };
};

export const updateCreditBalance = () => {
  return dispatch => {
    dispatch(fetchCreditBalancePendingAction(true));

    ApiClient.getCreditBalance(getUuid())
      .then(apiData => {
        dispatch(fetchCreditBalancePendingAction(false));
        dispatch(updateCreditBalanceAction(apiData.payload.amount));
      })
      .catch(apiError => {
        dispatch(fetchCreditBalancePendingAction(false));
      });
  };
};

export const logOutFromAllDevices = (name, email, onSuccess, onError) => {
  return dispatch => {
    ApiClient.logOutFromAllDevices(getUuid(), name, email)
      .then(apiData => {
        onSuccess();
        eventBus.dispatch("signOut");
      })
      .catch(apiError => {
        onError(apiError);
      });
  };
};

export const getAppStatus = onSuccess => {
  return dispatch => {
    ApiClient.getAppStatus(getUuid())
      .then(apiData => {
        onSuccess(false);
      })
      .catch(apiError => {
        onSuccess(apiError.httpStatus === 503);
      });
  };
};

export const verifyUser = (code, onSuccess, onError) => {
  return dispatch => {
    ApiClient.verifyUser(getUuid(), code)
      .then(apiData => {
        onSuccess(apiData.payload);
      })
      .catch(apiError => {
        onError(apiError);
      });
  };
};

export const getMultiuserList = () => {
  return dispatch => {
    dispatch(fetchMultiuserListPending(true));

    ApiClient.getMultiuserList(getUuid())
      .then(apiData => {
        dispatch(setMultiUserList(apiData.payload));
      })
      .catch(apiError => {
        dispatch(fetchMultiuserListPending(false));
      });
  };
};

export const sendMultiuserInvite = (name, email, access, onSuccess, onError) => {
  return (dispatch, getState) => {
    ApiClient.sendMultiuserInvite(getUuid(), name, email, access)
      .then(apiData => {
        var userList = multiuserListSelector(getState()) || [];
        userList.push(apiData.payload);
        dispatch(setMultiUserList(userList));
        onSuccess();
      })
      .catch(apiError => {
        onError(apiError);
      });
  };
};

export const updateMultiuserInvite = (user, propsToUpdate) => {
  return (dispatch, getState) => {
    var userList = multiuserListSelector(getState()) || [];
    var userToUpdateIndex = userList.findIndex(item => item.id === user.id);
    if (userToUpdateIndex === -1) {
      return;
    }

    userList[userToUpdateIndex] = { ...user, ...propsToUpdate };
    dispatch(setMultiUserList([...userList]));

    ApiClient.updateMultiuserInvite(getUuid(), user.id, propsToUpdate)
      .then(() => {})
      .catch(() => {
        userList[userToUpdateIndex] = user;
        dispatch(setMultiUserList([...userList]));
      });
  };
};

export const accpetMultiuserInvite = (invitationId, onSuccess, onError) => {
  return dispatch => {
    ApiClient.acceptMultiuserInvite(getUuid(), invitationId)
      .then(apiData => {
        onSuccess(apiData.payload);
      })
      .catch(apiError => {
        onError(apiError);
      });
  };
};

export const deleteSubAccountuser = (multiuserId, onSuccess, onError) => {
  return (dispatch, getState) => {
    ApiClient.deleteSubAccountuser(getUuid(), multiuserId)
      .then(apiData => {
        var userList = multiuserListSelector(getState()) || [];
        userList = userList.filter(item => item.id !== multiuserId);
        dispatch(setMultiUserList(userList));
        onSuccess();
      })
      .catch(apiError => {
        onError(apiError);
      });
  };
};

export const subAccountUserSelfLeave = (portfolioId, onSuccess, onError) => {
  return (dispatch, getState) => {
    ApiClient.subAccountUserSelfLeave(getUuid(), portfolioId)
      .then(() => {
        const user = userSelector(getState());
        var userList = multiuserListSelector(getState()) || [];

        if (!portfolioId === false) {
          userList = userList.filter(item => item.userId !== user.id || item.portfolioId !== portfolioId);
        } else {
          userList = userList.filter(item => item.userId !== user.id);
        }

        dispatch(setMultiUserList(userList));
        onSuccess();
      })
      .catch(apiError => {
        onError(apiError);
      });
  };
};

export const verifyMultiuserInvitationId = (email, invitationId, onSuccess, onError) => {
  return dispatch => {
    getRecaptchaToken("verify_multiuser_invitation")
      .then(recaptchaToken => {
        return ApiClient.verifyMultiuserInvitationId(getUuid(), email, invitationId, recaptchaToken);
      })
      .then(apiData => {
        onSuccess(apiData.payload.valid === 1);
      })
      .catch(error => {
        onError(error);
      });
  };
};

export const sendMultiuserReInvite = (multiuserId, onSuccess = null, onError = null) => {
  return (dispatch, getState) => {
    ApiClient.sendMultiuserReInvite(getUuid(), multiuserId)
      .then(apiData => {
        var userList = multiuserListSelector(getState()) || [];
        const userIndex = userList.findIndex(item => item.id === multiuserId);

        if (userIndex !== -1) {
          userList[userIndex] = apiData.payload;
          dispatch(setMultiUserList(userList));

          if (onSuccess) {
            onSuccess();
          }
        }
      })
      .catch(apiError => {
        if (onError) {
          onError(apiError);
        }
      });
  };
};

export const getReceipts = () => {
  return dispatch => {
    return new Promise((resolve, reject) => {
      ApiClient.getReceipts(getUuid())
        .then(data => {
          dispatch(setReceipts(data.payload));
          resolve(data.payload);
        })
        .catch(apiError => {
          reject(apiError);
        });
    });
  };
};

export const getReceiptUrl = invoiceId => {
  return dispatch => {
    return new Promise((resolve, reject) => {
      ApiClient.getReceiptUrl(getUuid(), invoiceId)
        .then(data => {
          resolve(data.payload);
        })
        .catch(apiError => {
          reject(apiError);
        });
    });
  };
};

export const injectReducersForSharedPortfolios = sharedPortfolioUsers => {
  const reducers = sharedPortfolioUsers.map(user => {
    const reducerName = portfoliosReducerName(user.id);
    const reducer = (state, action) => {
      action.portfolioUserId = user.id;
      return portfolioReducer({ ...portfoliosInitialState, ...state }, action);
    };
    return { key: reducerName, reducer: reducer, transform: portfolioPersistTransform(user.id) };
  });

  store.injectReducers(reducers);

  sharedPortfolioUsers.forEach(user => {
    store.dispatch(rehydrateRecapData(user.id));
    store.dispatch(rehydratePortfoliosData(user.id));
  });
};

export const switchToUsersDefaultAccount = () => {
  return (dispatch, getState) => {
    const user = userSelector(getState());

    setPortfolioSessionUserId(user.id);
    const url = `${window.location.protocol}//${window.location.host}`;
    window.location = url;
    window.name = null;
  };
};

export const switchToSharedPortfolioUserAccount = (user, inSameWindow = false) => {
  if (getPortfolioSessionUserId() === user.id) {
    return;
  }

  if (isMobile() || inSameWindow) {
    const url = `${window.location.protocol}//${window.location.host}`;
    window.location = url;

    setTimeout(() => {
      setPortfolioSessionUserId(user.id);
    }, 0);
    return;
  }

  const url = `${window.location.protocol}//${window.location.host}`;
  const sharedWindow = window.kuberaOpen(url, user.id + new Date().getTime());
  if (sharedWindow) {
    sharedWindow.portfolioUserId = user.id;
  }
};

const DEFAULT_OWNER_TAG_WAIT_TIMEOUT = 8000;

const waitForDefaultPersonTag = () =>
  new Promise(resolve => {
    if (!defaultPersonTagSelector(store.getState()) === false) {
      resolve();
      return;
    }
    let unsubscribe = null;
    const finish = () => {
      clearTimeout(timer);
      if (unsubscribe) {
        unsubscribe();
      }
      resolve();
    };
    const timer = setTimeout(finish, DEFAULT_OWNER_TAG_WAIT_TIMEOUT);
    unsubscribe = store.subscribe(() => {
      if (!defaultPersonTagSelector(store.getState()) === false) {
        finish();
      }
    });
  });

export const optInForBlackTrial = (onSuccess = () => null) => {
  return dispatch => {
    const request = idempotentId => ApiClient.optInForBlackTrial(idempotentId);
    const syncItem = new SyncItem(SyncItemType.UPDATE, "update_user", request, 0, true, apiData => {
      eventBus.dispatch("fetchPortfolios", [true]);
      waitForDefaultPersonTag().then(() => {
        onSuccess();
        setTimeout(() => {
          dispatch(setUserAction(apiData.payload));
        }, 600);
      });
    });
    dispatch(enqueueItem(syncItem));
  };
};

export const getUniqueSessionId = () => {
  return new Promise((resolve, reject) => {
    ApiClient.getUniqueSessionId(getUuid())
      .then(data => {
        resolve(data.payload?.sessionId);
      })
      .catch(apiError => {
        reject(apiError);
      });
  });
};

export const setUserAPIKeysAction = apiKeys => ({
  type: SET_USER_API_KEYS,
  apiKeys
});

const apiKeyWithoutCredentials = apiKey => {
  const { key, secret, authToken, ...rest } = apiKey;
  return rest;
};

export const getUserAPIKeys = () => {
  return dispatch =>
    new Promise((resolve, reject) => {
      ApiClient.getUserAPIKeys(getUuid())
        .then(data => {
          const apiKeys = data.payload || [];
          dispatch(setUserAPIKeysAction(apiKeys));
          resolve(apiKeys);
        })
        .catch(apiError => {
          reject(apiError);
        });
    });
};

export const createUserAPIKey = payload => {
  return (dispatch, getState) =>
    new Promise((resolve, reject) => {
      ApiClient.createUserAPIKey(getUuid(), { type: userApiKeyTypes.GENERIC, ...payload })
        .then(data => {
          const apiKey = data.payload;
          const apiKeys = userAPIKeysSelector(getState()) || [];
          dispatch(setUserAPIKeysAction([...apiKeys, apiKeyWithoutCredentials(apiKey)]));
          resolve(apiKey);
        })
        .catch(apiError => {
          reject(apiError);
        });
    });
};

export const deleteUserAPIKey = key => {
  return (dispatch, getState) =>
    new Promise((resolve, reject) => {
      ApiClient.deleteUserAPIKey(getUuid(), key)
        .then(data => {
          const apiKeys = userAPIKeysSelector(getState());
          if (!apiKeys === false) {
            dispatch(setUserAPIKeysAction(apiKeys.filter(apiKey => apiKey.id !== key)));
          }
          resolve(data.payload);
        })
        .catch(apiError => {
          reject(apiError);
        });
    });
};

export const getOAuth2Code = params => {
  return new Promise((resolve, reject) => {
    ApiClient.getOAuth2Code(getUuid(), params)
      .then(data => {
        resolve(data.payload);
      })
      .catch(apiError => {
        reject(apiError);
      });
  });
};

export const listOAuthConnections = () => {
  return dispatch =>
    new Promise((resolve, reject) => {
      ApiClient.listOAuthConnections(getUuid())
        .then(data => {
          const connections = (data?.payload || []).filter(
            connection => connection.tsCreated && connection.clientInfo?.name
          );
          dispatch({ type: SET_USER_OAUTH_CONNECTIONS, oAuthConnections: connections });
          resolve(connections);
        })
        .catch(apiError => {
          reject(apiError);
        });
    });
};

export const revokeOAuthConnection = oauthConnectionId => {
  return (dispatch, getState) =>
    new Promise((resolve, reject) => {
      ApiClient.revokeOAuthConnection(getUuid(), oauthConnectionId)
        .then(data => {
          const connections = userOAuthConnectionsSelector(getState()) || [];
          dispatch({
            type: SET_USER_OAUTH_CONNECTIONS,
            oAuthConnections: connections.filter(connection => connection.id !== oauthConnectionId)
          });
          resolve(data.payload);
        })
        .catch(apiError => {
          reject(apiError);
        });
    });
};

export const setRecaptchaVerifyResponse = (data, type) => {
  sessionStorage.setItem(
    "kuberaRecaptchaVerifyResponse",
    JSON.stringify({
      data,
      type
    })
  );
};

let alreadyFetchedStorageResponse = null;
export const getRecaptchaStoredResponse = () => {
  const kuberaRecaptchaVerifyResponse =
    alreadyFetchedStorageResponse || JSON.parse(sessionStorage.getItem("kuberaRecaptchaVerifyResponse"));
  if (kuberaRecaptchaVerifyResponse) {
    alreadyFetchedStorageResponse = kuberaRecaptchaVerifyResponse;
    return alreadyFetchedStorageResponse;
  }

  return null;
};

export const verifyV3Token = action => {
  return new Promise((resolve, reject) => {
    const storedResponse = getRecaptchaStoredResponse();
    if (storedResponse) {
      const { data, type } = storedResponse;
      if (type === "resolve") {
        resolve(data);
      } else {
        reject(data);
      }
      return;
    }
    getRecaptchaToken(action)
      .then(token => {
        return ApiClient.verifyV3Token(getUuid(), token);
      })
      .then(data => {
        const { error } = data.payload || { error: { code: "UserLambdaValidationException" } };
        if (error !== null) {
          setRecaptchaVerifyResponse(error, "reject");
          reject(error);
        } else {
          setRecaptchaVerifyResponse(true, "resolve");
          resolve(true);
        }
      })
      .catch(apiError => {
        setRecaptchaVerifyResponse(apiError, "reject");
        reject(apiError);
      });
  });
};

const eventMethods = { setAppMaintenanceStatus, fetchUser, setShowBreakingChangesError };

eventBus.addEventListener("any", event => {
  const { eventName, data } = event.detail;
  if (eventMethods[eventName]) {
    store.dispatch(eventMethods[eventName].apply(this, data));
  }
});

export const rehydrateUserPreferences = () => {
  return dispatch => {
    store.getStoredState().then(state => {
      const storedUserPreferences = userPreferencesSelector(state);
      dispatch(setUserPreferencesAction(storedUserPreferences));
    });
  };
};
